import { calendarWithKoreanHolidays } from "./calendar";
import type {
  CostOptions,
  Dependency,
  DependencyType,
  DocumentLink,
  ExecutionTask,
  PlanDocument,
  Project,
  ProjectRecord,
  Task,
} from "./types";

function costOptions(): CostOptions {
  return {
    workingDaysPerMonth: 21,
    includeOverhead: true,
    overheadRatio: 1.1,
    includeTechnicalFee: true,
    technicalFeeRatio: 0.2,
    hideRatesByDefault: false,
  };
}

function exec(id: string, title: string, extra: Partial<ExecutionTask>): ExecutionTask {
  const t0 = "2026-11-03T09:00:00.000Z";
  return {
    id,
    title,
    assignees: [],
    linkedTaskIds: [],
    labelIds: [],
    checklist: [],
    attachments: [],
    comments: [],
    activity: [],
    customFields: {},
    order: 0,
    createdAt: t0,
    updatedAt: t0,
    ...extra,
  };
}

/** 고객 마케팅 시스템 — 애자일 그로스, SaaS 중심 소형 예산. */
export function createSampleMarketingProject(): Project {
  const tasks: Task[] = [];
  const deps: Dependency[] = [];
  const add = (key: string, parent: string | null, name: string, duration: number, extra: Partial<Task> = {}) => {
    tasks.push({ id: key, parentId: parent, name, duration, percentComplete: 0, progressMethod: "percent", ...extra });
    return key;
  };
  let dseq = 0;
  const link = (p: string, s: string, type: DependencyType = "FS", lag = 0) => {
    dseq++;
    deps.push({ id: `mkt_d${dseq}`, predecessorId: p, successorId: s, type, lag });
  };

  add("mkt_p1", null, "디스커버리", 0, { percentComplete: 40 });
  add("mkt_t1-1", "mkt_p1", "페르소나·고객 여정 매핑", 8, {
    percentComplete: 60,
    assignee: "임그로스",
    assignedResourceId: "res_lim_growth",
    plannedMM: 0.4,
    deliverable: "여정 맵 v0.8",
  });
  add("mkt_t1-2", "mkt_p1", "기존 캠페인·CRM 감사", 6, {
    percentComplete: 50,
    assignee: "권CRM",
    assignedResourceId: "res_kwon_crm",
    plannedMM: 0.3,
  });
  add("mkt_t1-3", "mkt_p1", "성공 지표·실험 가드레일", 4, {
    percentComplete: 20,
    assignee: "윤데이터",
    assignedResourceId: "res_yoon_data",
    plannedMM: 0.2,
  });
  add("mkt_m1", "mkt_p1", "킥오프 승인", 0);
  link("mkt_t1-1", "mkt_t1-2", "SS", 2);
  link("mkt_t1-1", "mkt_t1-3", "SS", 3);
  link("mkt_t1-2", "mkt_m1");
  link("mkt_t1-3", "mkt_m1");

  add("mkt_p2", null, "CDP·데이터", 0, { percentComplete: 10 });
  add("mkt_t2-1", "mkt_p2", "CDP 스키마·식별자 설계", 8, {
    percentComplete: 15,
    assignee: "윤데이터",
    assignedResourceId: "res_yoon_data",
    plannedMM: 0.4,
  });
  add("mkt_t2-2", "mkt_p2", "웹/앱 이벤트 수집", 10, {
    percentComplete: 0,
    assignee: "서머신",
    assignedResourceId: "res_seo_ml",
    plannedMM: 0.4,
  });
  add("mkt_t2-3", "mkt_p2", "세그먼트·동의 관리", 7, {
    assignee: "권CRM",
    assignedResourceId: "res_kwon_crm",
    plannedMM: 0.35,
  });
  add("mkt_m2", "mkt_p2", "데이터 기반 확정", 0);
  link("mkt_m1", "mkt_t2-1");
  link("mkt_t2-1", "mkt_t2-2");
  link("mkt_t2-2", "mkt_t2-3", "SS", 3);
  link("mkt_t2-3", "mkt_m2");

  add("mkt_p3", null, "캠페인 플랫폼", 0);
  add("mkt_t3-1", "mkt_p3", "랜딩·에디터 화면", 12, {
    assignee: "정프론트",
    assignedResourceId: "res_jung_front",
    plannedMM: 0.6,
  });
  add("mkt_t3-2", "mkt_p3", "개인화 추천 엔진", 14, {
    assignee: "서머신",
    assignedResourceId: "res_seo_ml",
    plannedMM: 0.5,
  });
  add("mkt_t3-3", "mkt_p3", "CRM·메시지 연동", 10, {
    assignee: "권CRM",
    assignedResourceId: "res_kwon_crm",
    plannedMM: 0.5,
  });
  add("mkt_t3-4", "mkt_p3", "캠페인 카피·템플릿 세트", 8, {
    assignee: "장카피",
    assignedResourceId: "res_jang_copy",
    plannedMM: 0.4,
  });
  link("mkt_m2", "mkt_t3-1");
  link("mkt_t3-1", "mkt_t3-2", "SS", 4);
  link("mkt_t2-3", "mkt_t3-3");
  link("mkt_t3-1", "mkt_t3-4", "SS", 2);

  add("mkt_p4", null, "오픈·그로스", 0);
  add("mkt_t4-1", "mkt_p4", "A/B 실험 프레임", 6, {
    assignee: "윤데이터",
    assignedResourceId: "res_yoon_data",
    plannedMM: 0.3,
  });
  add("mkt_t4-2", "mkt_p4", "파일럿 캠페인 런칭", 8, {
    assignee: "임그로스",
    assignedResourceId: "res_lim_growth",
    plannedMM: 0.4,
  });
  add("mkt_t4-3", "mkt_p4", "회귀 테스트·동의 시나리오", 5, {
    assignee: "민테스터",
    assignedResourceId: "res_min_qa",
    plannedMM: 0.25,
  });
  add("mkt_m4", "mkt_p4", "오픈", 0, { deadline: "2027-04-15" });
  add("mkt_t4-5", "mkt_p4", "운영 이관 및 플레이북", 6, {
    assignee: "임그로스",
    assignedResourceId: "res_lim_growth",
    plannedMM: 0.3,
  });
  link("mkt_t3-2", "mkt_t4-1");
  link("mkt_t3-3", "mkt_t4-2");
  link("mkt_t3-4", "mkt_t4-2");
  link("mkt_t4-1", "mkt_t4-2", "SS", 2);
  link("mkt_t4-2", "mkt_t4-3", "SS");
  link("mkt_t4-3", "mkt_m4");
  link("mkt_m4", "mkt_t4-5");

  return {
    id: "sample-marketing",
    name: "고객 마케팅 시스템",
    start: "2026-10-05",
    statusDate: "2026-10-20",
    calendar: calendarWithKoreanHolidays(),
    tasks,
    dependencies: deps,
    baselines: [],
    activeBaselineId: null,
    criticalThreshold: 0,
    executionTasks: [
      exec("mkt_x1", "페르소나 워크숍 일정 잡기", {
        status: "inProgress",
        priority: "important",
        bucketId: "b_now",
        assignees: ["임그로스"],
        start: "2026-11-04",
        due: "2026-11-12",
        linkedTaskIds: ["mkt_t1-1"],
        labelIds: ["l_growth"],
        checklist: [
          { id: "mkt_ck1", text: "세일즈 2명 초대", done: true },
          { id: "mkt_ck2", text: "기존 세그먼트 출력", done: false },
        ],
        order: 0,
      }),
      exec("mkt_x2", "Braze 워크스페이스 권한 요청", {
        status: "notStarted",
        priority: "medium",
        bucketId: "b_backlog",
        assignees: ["권CRM"],
        due: "2026-11-20",
        linkedTaskIds: ["mkt_t3-3"],
        labelIds: ["l_crm"],
        order: 0,
      }),
      exec("mkt_x3", "온보딩 메일 초안 3종", {
        status: "notStarted",
        priority: "medium",
        bucketId: "b_backlog",
        assignees: ["장카피"],
        linkedTaskIds: ["mkt_t3-4"],
        labelIds: ["l_copy"],
        order: 1,
      }),
      exec("mkt_x4", "기존 CRM 필드 목록 정리", {
        status: "done",
        priority: "low",
        bucketId: "b_done",
        assignees: ["권CRM"],
        start: "2026-10-10",
        due: "2026-10-16",
        completedAt: "2026-10-15T08:00:00.000Z",
        linkedTaskIds: ["mkt_t1-2"],
        labelIds: ["l_crm"],
        order: 0,
      }),
      exec("mkt_x_me_roster", "킥오프 참석자 확정", {
        status: "inProgress",
        priority: "important",
        bucketId: "b_now",
        assignees: ["나"],
        start: "2026-10-13",
        due: "2026-10-16",
        linkedTaskIds: ["mkt_t1-1"],
        labelIds: ["l_growth"],
        checklist: [
          { id: "mkt_ck_me1", text: "세일즈 리드", done: true },
          { id: "mkt_ck_me2", text: "법무 수신동의", done: false },
        ],
        order: 0,
      }),
      exec("mkt_x_me_metric", "성공 지표 초안 리뷰", {
        status: "notStarted",
        priority: "urgent",
        bucketId: "b_now",
        assignees: ["나"],
        start: "2026-10-19",
        due: "2026-10-19",
        linkedTaskIds: ["mkt_t1-3"],
        labelIds: ["l_data"],
        notes: "실험 가드레일과 파일럿 예산을 같은 자리에서 본다.",
        order: 0,
      }),
      exec("mkt_x_me_budget", "파일럿 예산 가드레일 합의", {
        status: "notStarted",
        priority: "important",
        bucketId: "b_now",
        assignees: ["나"],
        start: "2026-10-21",
        due: "2026-10-23",
        linkedTaskIds: ["mkt_t4-2"],
        labelIds: ["l_growth"],
        order: 2,
      }),
      exec("mkt_x_lim_review", "여정 맵 리뷰 회의", {
        status: "inProgress",
        priority: "important",
        bucketId: "b_now",
        assignees: ["임그로스"],
        start: "2026-10-20",
        due: "2026-10-21",
        linkedTaskIds: ["mkt_t1-1"],
        labelIds: ["l_growth"],
        order: 2,
      }),
      exec("mkt_x_legal", "Braze 계약 법무 회신 대기", {
        status: "blocked",
        priority: "urgent",
        bucketId: "b_review",
        assignees: ["권CRM"],
        due: "2026-10-17",
        linkedTaskIds: ["mkt_t3-3"],
        labelIds: ["l_crm"],
        notes: "법무 회신 없이 워크스페이스를 열 수 없다.",
        comments: [{ id: "mkt_cm_legal", author: "권CRM", body: "개인정보 국외 이전이 쟁점입니다. 10/21 재검토.", at: "2026-10-17T06:00:00.000Z" }],
        order: 0,
      }),
    ],
    planner: {
      buckets: [
        { id: "b_backlog", name: "아이디어" },
        { id: "b_now", name: "이번 스프린트" },
        { id: "b_review", name: "실험 대기" },
        { id: "b_done", name: "끝난 일" },
      ],
      labels: [
        { id: "l_growth", name: "그로스", color: "purple" },
        { id: "l_crm", name: "CRM", color: "blue" },
        { id: "l_copy", name: "카피", color: "orange" },
        { id: "l_data", name: "데이터", color: "teal" },
      ],
      customFields: [],
    },
    records: sampleMarketingRecords(),
    documents: sampleMarketingDocuments(),
    documentLinks: sampleMarketingDocumentLinks(),
    resourceAssignments: [
      {
        id: "asg_mkt_lim",
        resourceId: "res_lim_growth",
        projectRole: "그로스 PM",
        accessRole: "pm",
        startDate: "2026-10-05",
        endDate: "2027-04-30",
        allocationRatio: 1.0,
        plannedMM: 6.0,
      },
      {
        id: "asg_mkt_me",
        resourceId: "res_me",
        projectRole: "사업 소유자",
        accessRole: "admin",
        startDate: "2026-10-05",
        endDate: "2027-04-30",
        allocationRatio: 0.3,
        plannedMM: 2.0,
      },
      {
        id: "asg_mkt_kwon",
        resourceId: "res_kwon_crm",
        projectRole: "CRM / 여정 PL",
        accessRole: "pl",
        startDate: "2026-10-05",
        endDate: "2027-04-15",
        allocationRatio: 0.8,
        plannedMM: 4.4,
      },
      {
        id: "asg_mkt_jang",
        resourceId: "res_jang_copy",
        projectRole: "캠페인 카피 (프리랜서)",
        accessRole: "member",
        startDate: "2026-12-01",
        endDate: "2027-03-31",
        allocationRatio: 0.6,
        plannedMM: 2.4,
      },
      {
        id: "asg_mkt_jung",
        resourceId: "res_jung_front",
        projectRole: "랜딩·에디터 UI",
        accessRole: "member",
        startDate: "2026-12-15",
        endDate: "2027-03-15",
        allocationRatio: 0.4,
        plannedMM: 1.2,
      },
      {
        id: "asg_mkt_yoon",
        resourceId: "res_yoon_data",
        projectRole: "실험 설계 / CDP",
        accessRole: "pl",
        startDate: "2026-10-05",
        endDate: "2027-04-30",
        allocationRatio: 0.5,
        plannedMM: 3.0,
      },
      {
        id: "asg_mkt_seo",
        resourceId: "res_seo_ml",
        projectRole: "개인화 모델",
        accessRole: "member",
        startDate: "2027-01-05",
        endDate: "2027-04-15",
        allocationRatio: 0.3,
        plannedMM: 1.0,
      },
      {
        id: "asg_mkt_min",
        resourceId: "res_min_qa",
        projectRole: "동의·회귀 테스트",
        accessRole: "member",
        startDate: "2027-02-01",
        endDate: "2027-04-15",
        allocationRatio: 0.3,
        plannedMM: 0.8,
      },
    ],
    solutions: [
      {
        id: "sol_mkt_braze",
        name: "Braze 마케팅 자동화",
        category: "saas",
        vendor: "Braze",
        billingCycle: "monthly",
        unitCost: 3800000,
        quantity: 6,
        totalCost: 22800000,
        startDate: "2026-10-05",
        endDate: "2027-04-30",
        assignedResourceId: "res_kwon_crm",
        status: "planned",
      },
      {
        id: "sol_mkt_segment",
        name: "Twilio Segment CDP",
        category: "saas",
        vendor: "Twilio",
        billingCycle: "monthly",
        unitCost: 2100000,
        quantity: 6,
        totalCost: 12600000,
        startDate: "2026-10-05",
        endDate: "2027-04-30",
        assignedResourceId: "res_yoon_data",
        status: "planned",
      },
      {
        id: "sol_mkt_amp",
        name: "Amplitude 실험·분석",
        category: "saas",
        vendor: "Amplitude",
        billingCycle: "monthly",
        unitCost: 1600000,
        quantity: 6,
        totalCost: 9600000,
        startDate: "2026-11-15",
        endDate: "2027-04-30",
        assignedResourceId: "res_yoon_data",
        status: "planned",
      },
      {
        id: "sol_mkt_ads",
        name: "검색·소셜 파일럿 광고비",
        category: "saas",
        vendor: "Google / Meta",
        billingCycle: "monthly",
        unitCost: 2500000,
        quantity: 3,
        totalCost: 7500000,
        startDate: "2027-02-01",
        endDate: "2027-04-30",
        assignedResourceId: "res_lim_growth",
        status: "planned",
        notes: "오픈 직후 3개월 파일럿",
      },
      {
        id: "sol_mkt_figma",
        name: "Figma Professional",
        category: "saas",
        vendor: "Figma Inc",
        billingCycle: "monthly",
        unitCost: 45000,
        quantity: 24,
        totalCost: 1080000,
        startDate: "2026-10-05",
        endDate: "2027-04-30",
        assignedResourceId: "res_jung_front",
        status: "active",
      },
    ],
    budget: {
      totalBudget: 240000000,
      targetLaborCost: 150000000,
      targetSolutionCost: 60000000,
      targetExpenseCost: 20000000,
      contingencyCost: 10000000,
      costOptions: costOptions(),
    },
    updatedAt: new Date().toISOString(),
  };
}

function sampleMarketingRecords(): ProjectRecord[] {
  const at = "2026-10-12T01:00:00.000Z";
  const base = (id: string, kind: ProjectRecord["kind"], seq: number, title: string, extra: Partial<ProjectRecord>): ProjectRecord => ({
    id,
    kind,
    seq,
    title,
    status: "open",
    assignees: [],
    impacts: [],
    sections: {},
    linkedTaskIds: [],
    linkedExecutionTaskIds: [],
    linkedRecordIds: [],
    attachments: [],
    comments: [],
    activity: [{ id: `${id}_ac0`, at, actor: "나", message: "생성" }],
    createdAt: at,
    updatedAt: at,
    ...extra,
  });
  return [
    base("mkt_iss_field", "issue", 1, "기존 CRM 필드 이름이 화면과 다름", {
      status: "inProgress",
      severity: "medium",
      source: "internal",
      assignees: ["권CRM"],
      due: "2026-10-23",
      impacts: ["scope"],
      sections: {
        summary: "여정 맵에 쓴 세그먼트 키가 CRM 실제 필드와 3건 다르다.",
        cause: "작년에 이름을 바꿨는데 카탈로그가 그대로다.",
      },
      linkedTaskIds: ["mkt_t1-2", "mkt_t2-1"],
      linkedExecutionTaskIds: ["mkt_x4"],
      comments: [{ id: "mkt_iss_field_cm1", author: "권CRM", body: "불일치 3건은 10/15 목록에 표시해 두었습니다.", at: "2026-10-15T08:30:00.000Z" }],
    }),
    base("mkt_iss_legal", "issue", 2, "Braze 계약 법무 회신 지연", {
      status: "open",
      severity: "high",
      source: "client",
      assignees: ["권CRM", "나"],
      due: "2026-10-21",
      impacts: ["schedule"],
      sections: {
        summary: "개인정보 국외 이전 검토가 끝나기 전에는 워크스페이스를 열 수 없다.",
      },
      linkedTaskIds: ["mkt_t3-3"],
      linkedExecutionTaskIds: ["mkt_x_legal"],
    }),
    base("mkt_rsk_consent", "risk", 1, "수신 동의 시나리오가 오픈에 못 맞을 수 있음", {
      status: "identified",
      severity: "high",
      source: "regulation",
      assignees: ["임그로스"],
      due: "2026-11-20",
      impacts: ["schedule", "quality"],
      sections: {
        summary: "동의 문구와 철회 동선이 파일럿 전에 확정되지 않으면 발송을 열 수 없다.",
        mitigation: "법무 회신과 별도로 동의 초안을 먼저 리뷰한다.",
      },
      linkedTaskIds: ["mkt_t2-3", "mkt_t4-3"],
    }),
    base("mkt_rsk_budget", "risk", 2, "파일럿 광고비가 가드레일을 넘을 수 있음", {
      status: "mitigating",
      severity: "medium",
      source: "internal",
      assignees: ["나"],
      due: "2026-10-23",
      impacts: ["cost"],
      sections: {
        summary: "검색·소셜 3개월 예산이 실험 수에 비해 빠듯하다.",
        mitigation: "주간 상한을 두고, 넘는 실험은 다음 주로 미룬다.",
      },
      linkedTaskIds: ["mkt_t4-2"],
      linkedExecutionTaskIds: ["mkt_x_me_budget"],
    }),
    base("mkt_dec_channel", "decision", 1, "발송 채널은 Braze, 분석은 Amplitude", {
      status: "proposed",
      source: "internal",
      assignees: ["임그로스", "윤데이터"],
      due: "2026-10-28",
      impacts: ["cost", "scope"],
      sections: {
        context: "자체 발송을 만들면 오픈이 6주 늦어진다.",
        decision: "메시지 발송은 Braze, 실험 판정은 Amplitude로 나눈다.",
        alternatives: "전부 자체 구축 — 범위 밖.",
      },
      linkedTaskIds: ["mkt_t3-3", "mkt_t4-1"],
      linkedRecordIds: ["mkt_iss_legal"],
    }),
    base("mkt_pol_consent", "policy", 1, "마케팅 수신 동의와 철회", {
      status: "proposed",
      source: "regulation",
      assignees: ["권CRM"],
      due: "2026-11-06",
      sections: {
        statement: "광고성 메시지는 동의한 사람에게만 보내고, 철회는 메시지 안에서 한 번에 된다.",
        applicability: "이메일·앱 푸시·문자.",
        exceptions: "거래 안내는 동의와 무관하게 발송한다.",
      },
      linkedTaskIds: ["mkt_t2-3"],
    }),
    base("mkt_chg_persona", "change", 1, "페르소나에 휴면 고객 세그먼트 추가", {
      status: "requested",
      severity: "low",
      source: "client",
      assignees: ["임그로스"],
      due: "2026-10-30",
      impacts: ["scope"],
      sections: {
        summary: "90일 미구매 고객을 여정 맵의 별도 페르소나로 둔다.",
        reason: "세일즈가 윈백 캠페인을 파일럿에 넣고 싶어 한다.",
        impactAnalysis: "여정 맵 한 갈래 추가. 발송 템플릿 1종.",
      },
      linkedTaskIds: ["mkt_t1-1"],
      linkedExecutionTaskIds: ["mkt_x1", "mkt_x_lim_review"],
    }),
  ];
}

function sampleMarketingDocuments(): PlanDocument[] {
  return [
    {
      id: "mkt_doc_kickoff",
      seq: 1,
      title: "고객 마케팅 시스템 킥오프 및 페르소나 정의 회의",
      kind: "meeting",
      visibility: "plan",
      authorId: "임그로스",
      heldAt: "2026-10-14",
      blocks: [
        {
          id: "b_k1",
          type: "heading",
          props: { level: 2 },
          content: [{ type: "text", text: "1. 참석자 및 회의 배경", styles: {} }],
        },
        {
          id: "b_k2",
          type: "paragraph",
          content: [
            {
              type: "text",
              text: "초기 온보딩 이탈률을 낮추기 위해 세일즈·CRM·데이터 파트가 모여 1차 핵심 페르소나와 여정 가설을 정렬했습니다.",
              styles: {},
            },
          ],
        },
        {
          id: "b_k3",
          type: "heading",
          props: { level: 2 },
          content: [{ type: "text", text: "2. 주요 논의 사항", styles: {} }],
        },
        {
          id: "b_k4",
          type: "bulletListItem",
          content: [{ type: "text", text: "신규 가입 후 7일 이내 첫 캠페인 생성 여부를 핵심 전환 지표로 둔다.", styles: {} }],
        },
        {
          id: "b_k5",
          type: "bulletListItem",
          content: [{ type: "text", text: "기존 CRM 세그먼트 필드명과 화면 표기가 다른 항목 3건은 이번 주 내 정리한다.", styles: {} }],
        },
        {
          id: "b_k6",
          type: "checkListItem",
          props: { checked: true },
          content: [{ type: "text", text: "세일즈 리드 및 법무 담당자 킥오프 명단 확정", styles: {} }],
        },
        {
          id: "b_k7",
          type: "checkListItem",
          props: { checked: false },
          content: [{ type: "text", text: "90일 휴면 고객 윈백 시나리오를 별도 페르소나로 분리할지 여부 검토", styles: {} }],
        },
      ],
      plainText:
        "1. 참석자 및 회의 배경\n초기 온보딩 이탈률을 낮추기 위해 세일즈·CRM·데이터 파트가 모여 1차 핵심 페르소나와 여정 가설을 정렬했습니다.\n2. 주요 논의 사항\n신규 가입 후 7일 이내 첫 캠페인 생성 여부를 핵심 전환 지표로 둔다.\n기존 CRM 세그먼트 필드명과 화면 표기가 다른 항목 3건은 이번 주 내 정리한다.\n세일즈 리드 및 법무 담당자 킥오프 명단 확정\n90일 휴면 고객 윈백 시나리오를 별도 페르소나로 분리할지 여부 검토",
      attachments: [],
      comments: [
        {
          id: "mkt_doc_cm1",
          author: "권CRM",
          body: "CRM 필드 매핑 표는 정리 완료하여 이슈 대장에 연결해 두었습니다.",
          at: "2026-10-15T09:20:00.000Z",
        },
      ],
      activity: [{ id: "mkt_doc_ac1", at: "2026-10-14T10:00:00.000Z", actor: "임그로스", message: "문서를 만들었습니다" }],
      createdAt: "2026-10-14T10:00:00.000Z",
      updatedAt: "2026-10-15T09:20:00.000Z",
    },
    {
      id: "mkt_doc_guardrail",
      seq: 2,
      title: "성공 지표 및 파일럿 예산 가드레일 초안",
      kind: "memo",
      visibility: "plan",
      authorId: "나",
      blocks: [
        {
          id: "b_g1",
          type: "heading",
          props: { level: 2 },
          content: [{ type: "text", text: "북극성 지표와 실험 가드레일", styles: {} }],
        },
        {
          id: "b_g2",
          type: "paragraph",
          content: [
            {
              type: "text",
              text: "파일럿 3개월 동안 주간 광고비 상한을 설정하고, 수신 거부율이 0.8%를 넘는 실험군은 즉시 발송을 멈춘다.",
              styles: {},
            },
          ],
        },
        {
          id: "b_g3",
          type: "quote",
          content: [
            {
              type: "text",
              text: "예산 소진 속도보다 첫 14일 리텐션 개선 폭이 확인된 실험군에만 후속 예산을 배정한다.",
              styles: {},
            },
          ],
        },
      ],
      plainText:
        "북극성 지표와 실험 가드레일\n파일럿 3개월 동안 주간 광고비 상한을 설정하고, 수신 거부율이 0.8%를 넘는 실험군은 즉시 발송을 멈춘다.\n예산 소진 속도보다 첫 14일 리텐션 개선 폭이 확인된 실험군에만 후속 예산을 배정한다.",
      attachments: [],
      comments: [],
      activity: [{ id: "mkt_doc_ac2", at: "2026-10-19T14:00:00.000Z", actor: "나", message: "문서를 만들었습니다" }],
      createdAt: "2026-10-19T14:00:00.000Z",
      updatedAt: "2026-10-20T11:30:00.000Z",
    },
    {
      id: "mkt_doc_copy",
      seq: 3,
      title: "온보딩 메일 시퀀스 3종 카피 레퍼런스",
      kind: "material",
      visibility: "private",
      authorId: "나",
      blocks: [
        {
          id: "b_c1",
          type: "paragraph",
          content: [
            {
              type: "text",
              text: "가입 직후(D+0), 첫 세그먼트 연동 안내(D+2), 템플릿 추천(D+5) 3단계 메시지 톤앤매너 초안입니다.",
              styles: {},
            },
          ],
        },
      ],
      plainText: "가입 직후(D+0), 첫 세그먼트 연동 안내(D+2), 템플릿 추천(D+5) 3단계 메시지 톤앤매너 초안입니다.",
      attachments: [],
      comments: [],
      activity: [{ id: "mkt_doc_ac3", at: "2026-10-20T16:00:00.000Z", actor: "나", message: "문서를 만들었습니다" }],
      createdAt: "2026-10-20T16:00:00.000Z",
      updatedAt: "2026-10-20T16:00:00.000Z",
    },
  ];
}

function sampleMarketingDocumentLinks(): DocumentLink[] {
  return [
    {
      id: "mkt_dl_1",
      documentId: "mkt_doc_kickoff",
      targetKind: "executionTask",
      targetId: "mkt_x1",
      origins: ["explicit"],
      snapshot: { title: "페르소나 워크숍 일정 잡기" },
      createdAt: "2026-10-14T10:30:00.000Z",
      createdBy: "임그로스",
    },
    {
      id: "mkt_dl_2",
      documentId: "mkt_doc_kickoff",
      targetKind: "record",
      targetId: "mkt_iss_field",
      origins: ["explicit"],
      snapshot: { code: "ISS-1", title: "기존 CRM 필드 이름이 화면과 다름" },
      createdAt: "2026-10-15T09:00:00.000Z",
      createdBy: "권CRM",
    },
    {
      id: "mkt_dl_3",
      documentId: "mkt_doc_guardrail",
      targetKind: "executionTask",
      targetId: "mkt_x_me_metric",
      origins: ["explicit"],
      snapshot: { title: "성공 지표 초안 리뷰" },
      createdAt: "2026-10-19T14:10:00.000Z",
      createdBy: "나",
    },
    {
      id: "mkt_dl_4",
      documentId: "mkt_doc_guardrail",
      targetKind: "executionTask",
      targetId: "mkt_x_me_budget",
      origins: ["explicit"],
      snapshot: { title: "파일럿 예산 가드레일 합의" },
      createdAt: "2026-10-19T14:15:00.000Z",
      createdBy: "나",
    },
    {
      id: "mkt_dl_5",
      documentId: "mkt_doc_copy",
      targetKind: "executionTask",
      targetId: "mkt_x3",
      origins: ["explicit"],
      snapshot: { title: "온보딩 메일 초안 3종" },
      createdAt: "2026-10-20T16:05:00.000Z",
      createdBy: "나",
    },
  ];
}
